// src/pages/business/products/ProductList.tsx
// Mimics the mobile ProductManagementScreen with business portal styling

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  X,
  BarChart2,
  List,
  Grid2X2,
  LayoutGrid,
  Package,
  Plus,
  Printer,
  ShoppingCart,
  Check,
  Loader2,
  RefreshCw,
  Barcode,
  ScanBarcode,
  Lock,
  Unlock,
  History,
  Bluetooth,
  CheckSquare,
  Square,
  Trash2
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl, extractImageUrls } from '@/config/media';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useBusinessStore } from '@/stores/useBusinessStore';
import { usePOSCartStore } from '@/stores/usePOSCartStore';
import { POSCartSidebar } from '@/components/business/livepos/POSCartSidebar';
import { useToast } from '@/contexts/ToastContext';

type ColCount = 1 | 2 | 3 | 4;

const COLS: ColCount[] = [1, 2, 3, 4];

const stockColor = (stock: number, threshold: number) =>
  stock === 0 ? '#ef4444' : stock <= threshold ? '#f59e0b' : '#10b981';

// ── Grid card ─────────────────────────────────────────────────────────────────
const ProductCard: React.FC<{
  item: any;
  cols: ColCount;
  onPress: () => void;
  onCart: () => void;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}> = ({ item, cols, onPress, onCart, isSelectMode, isSelected, onToggleSelect }) => {
  // extractImageUrls() already returns full URLs, don't call getImageUrl() again
  const thumb = extractImageUrls(item.media_urls)[0] || item.image_url || null;
  
  const stock = item.quantity_in_stock ?? 0;
  const threshold = item.low_stock_threshold ?? 5;
  const sc = stockColor(stock, threshold);

  const handleCardClick = () => {
    if (isSelectMode && onToggleSelect) {
      onToggleSelect();
    } else {
      onPress();
    }
  };

  // 1-col: full detail card (list view)
  if (cols === 1) {
    return (
      <button
        onClick={handleCardClick}
        className={`w-full flex items-center gap-3 p-3 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border text-left relative ${
          isSelected ? 'bg-primary/5 dark:bg-primary/10 border-primary/30 dark:border-primary/50' : ''
        }`}
      >
        {isSelectMode && (
          <div className="flex-shrink-0 mr-1">
            {isSelected ? (
              <CheckSquare size={20} className="text-primary dark:text-golden" />
            ) : (
              <Square size={20} className="text-light-subtle dark:text-dark-subtle" />
            )}
          </div>
        )}
        {/* Thumb 70x70 */}
        <div
          className="relative flex-shrink-0 rounded-lg overflow-hidden"
          style={{ width: 70, height: 70 }}
        >
          {thumb ? (
            <img
              src={thumb}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-light-bg dark:bg-dark-bg">
              <Package size={24} className="text-light-subtle dark:text-dark-subtle" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm line-clamp-2 text-light-text dark:text-dark-text mb-1">
            {item.name}
          </p>
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: sc + '22', color: sc }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ backgroundColor: sc }}
              />
              {stock === 0 ? 'Out' : stock <= threshold ? 'Low' : 'In Stock'} · {stock}
            </span>
            {item.price != null && (
              <span className="font-bold text-xs text-primary dark:text-golden">
                KES {item.price.toLocaleString()}
              </span>
            )}
          </div>
          {item.category && (
            <p className="text-xs text-light-subtle dark:text-dark-subtle">{item.category}</p>
          )}
        </div>

        {/* Actions */}
        {!isSelectMode && stock > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCart();
            }}
            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{
              backgroundColor: '#4F46E5',
            }}
          >
            <ShoppingCart size={16} className="text-white" />
          </button>
        )}
      </button>
    );
  }

  // 2/3/4-col grid
  const imgSize = cols === 2 ? 140 : cols === 3 ? 100 : 80;
  return (
    <div className="flex flex-col text-left" style={{ width: imgSize }}>
      <div
        className={`relative rounded-lg overflow-hidden mb-2 cursor-pointer bg-light-input dark:bg-dark-input ${
          isSelected ? 'ring-2 ring-primary dark:ring-golden ring-offset-2 ring-offset-light-bg dark:ring-offset-dark-bg' : ''
        }`}
        style={{ width: imgSize, height: imgSize }}
        onClick={handleCardClick}
      >
        {isSelectMode && (
          <div className="absolute top-1.5 right-1.5 z-10 bg-white dark:bg-gray-800 rounded-sm">
            {isSelected ? (
              <CheckSquare size={18} className="text-primary dark:text-golden" />
            ) : (
              <Square size={18} className="text-gray-400" />
            )}
          </div>
        )}
        {thumb ? (
          <img
            src={thumb}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package
              size={cols === 2 ? 24 : 18}
              className="text-light-subtle dark:text-dark-subtle"
            />
          </div>
        )}
        {/* Stock dot */}
        <span
          className="absolute top-1.5 left-1.5 w-2 h-2 rounded-full border border-white"
          style={{ backgroundColor: sc }}
        />
        {!isSelectMode && cols === 2 && stock > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCart();
            }}
            className="absolute bottom-1 right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-md"
            style={{ backgroundColor: '#4F46E5' }}
          >
            <ShoppingCart size={13} className="text-white" />
          </button>
        )}
      </div>
      <div onClick={handleCardClick} className="cursor-pointer">
        <p
          className="font-medium text-xs line-clamp-2 text-light-text dark:text-dark-text"
          style={{ fontSize: cols === 4 ? 9 : cols === 3 ? 10 : 11 }}
        >
          {item.name}
        </p>
        {cols <= 2 && item.price != null && (
          <p className="font-bold text-xs text-primary dark:text-golden mt-1">
            KES {item.price.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Screen ────────────────────────────────────────────────────────────────────
const ProductList: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toastCtx = useToast();

  const { products, fetchProducts, deleteProduct } = useBusinessStore();
  const { addItem, scanItem } = usePOSCartStore();

  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const [loading, setLoading] = useState(products.length === 0);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [cols, setCols] = useState<ColCount>(2);
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeystrokeTime, setLastKeystrokeTime] = useState(0);
  const [isPosLocked, setIsPosLocked] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // VS Code-style resizable sidebar
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(400);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = dragStartXRef.current - ev.clientX; // dragging left = wider sidebar
      const newWidth = Math.min(600, Math.max(280, dragStartWidthRef.current + delta));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth]);

  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;

    if (products.length === 0) {
      setLoading(true);
      fetchProducts(businessId).finally(() => {
        if (!cancelled) setLoading(false);
      });
    } else {
      setLoading(false);
      // Background refresh without blocking the UI
      fetchProducts(businessId);
    }

    return () => {
      cancelled = true;
      setLoading(false); // Always clear spinner on unmount/route-change
    };
  }, [businessId, fetchProducts]);

  const handleRefresh = async () => {
    if (!businessId) return;
    setRefreshing(true);
    await fetchProducts(businessId);
    setRefreshing(false);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const toggleSelectAll = () => {
    if (selectedProductIds.length === filtered.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(filtered.map((p) => p.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProductIds.length === 0 || !businessId) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedProductIds.length} products?`)) return;

    try {
      const deletePromises = selectedProductIds.map(id => deleteProduct(businessId, id));
      await Promise.all(deletePromises);
      toastCtx.success('Deleted', `Successfully deleted ${selectedProductIds.length} products`);
      setSelectedProductIds([]);
      setIsSelectMode(false);
    } catch (err: any) {
      toastCtx.error('Error', err.message || 'Failed to delete some products');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        return;
      }

      const currentTime = new Date().getTime();

      if (e.key === 'Enter') {
        if (barcodeBuffer.length > 3) {
          processBarcode(barcodeBuffer);
        }
        setBarcodeBuffer('');
      } else if (e.key.length === 1) {
        if (currentTime - lastKeystrokeTime > 100) {
          setBarcodeBuffer(e.key);
        } else {
          setBarcodeBuffer(prev => prev + e.key);
        }
        setLastKeystrokeTime(currentTime);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [barcodeBuffer, lastKeystrokeTime, businessId]);

  const processBarcode = async (scannedCode: string) => {
    if (!businessId) return;
    try {
      toastCtx.info('Scanner', `Scanning ${scannedCode}...`);
      await scanItem(businessId, scannedCode);
      toastCtx.success('Success', `Added to cart`);
    } catch (err: any) {
      toastCtx.error('Error', err.response?.data?.error || 'Item not found');
    }
  };

  const addToCart = async (product: any) => {
    if (!businessId) return;
    try {
      await addItem(businessId, product, 1);
      toastCtx.success('Success', `${product.name} added to cart`);
    } catch {
      // silent fail if store handles error
    }
  };

  const colIcon =
    cols === 1 ? (
      <List size={20} />
    ) : cols === 2 ? (
      <Grid2X2 size={20} />
    ) : cols === 3 ? (
      <LayoutGrid size={20} />
    ) : (
      <LayoutGrid size={18} />
    );

  const active = filtered.filter((p) => p.is_active).length;
  const low = filtered.filter(
    (p) => p.quantity_in_stock <= (p.low_stock_threshold ?? 5) && p.quantity_in_stock > 0
  ).length;
  const out = filtered.filter((p) => p.quantity_in_stock === 0).length;

  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden bg-light-bg dark:bg-dark-bg">
      {/* Header - Full Width */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => navigate(businessId ? `/business/${businessId}` : '/business')}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} className="text-primary dark:text-golden" />
            </button>

            {/* Search Bar */}
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg">
              <Search
                size={16}
                className="text-light-subtle dark:text-dark-subtle flex-shrink-0"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${products.length} products…`}
                className="flex-1 bg-transparent text-sm text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch('')}>
                  <X size={16} className="text-light-subtle dark:text-dark-subtle" />
                </button>
              )}
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/business/${businessId}/products/new`)}
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition shadow-sm font-semibold text-sm"
              >
                <Plus size={18} />
                <span>Add Product</span>
              </button>
              
              <button
                onClick={() => {
                  if (isSelectMode) {
                    setIsSelectMode(false);
                    setSelectedProductIds([]);
                  } else {
                    setIsSelectMode(true);
                  }
                }}
                className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 border rounded-lg transition shadow-sm font-semibold text-sm ${
                  isSelectMode 
                    ? 'bg-primary text-white border-primary hover:bg-primary/90' 
                    : 'border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
                }`}
              >
                {isSelectMode ? <X size={18} /> : <CheckSquare size={18} />}
                <span>{isSelectMode ? 'Cancel' : 'Select'}</span>
              </button>

              <div className="flex items-center gap-3">
              <button
                onClick={() => setIsPosLocked(!isPosLocked)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border transition ${
                  isPosLocked 
                    ? 'bg-primary/10 border-primary/30 text-primary' 
                    : 'bg-light-bg dark:bg-dark-bg border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={isPosLocked ? "POS Locked - Tap adds to cart" : "POS Unlocked - Tap opens details"}
              >
                {isPosLocked ? <Lock size={16} /> : <Unlock size={16} />}
              </button>
              <button
                onClick={() => setCols(cols === 1 ? 2 : cols === 2 ? 3 : cols === 3 ? 4 : 1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                title="Toggle columns"
              >
                {colIcon}
              </button>
              </div>

              <div className="h-6 w-px bg-light-border dark:bg-dark-border mx-1 hidden md:block"></div>

            <ThemeToggle />
          </div>
        </div>

        {/* Stats Strip & Bulk Actions */}
        {!loading && products.length > 0 && (
          <div className="border-t border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg px-4 py-2 flex items-center justify-between">
            {isSelectMode ? (
              <div className="flex items-center gap-3 w-full animate-fade-in">
                <button
                  onClick={toggleSelectAll}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                  {selectedProductIds.length === filtered.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-xs font-medium text-light-text dark:text-dark-text">
                  {selectedProductIds.length} selected
                </span>
                <div className="flex-1"></div>
                {selectedProductIds.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition"
                  >
                    <Trash2 size={14} />
                    Delete Selected
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-light-subtle dark:text-dark-subtle">
                  <span className="font-bold text-primary dark:text-golden">
                    {filtered.length}
                  </span>
                  {search ? ' found' : ' products'}
                </span>
                <span className="w-px h-3 bg-light-border dark:border-dark-border" />
                <span className="text-light-subtle dark:text-dark-subtle">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    {active}
                  </span>{' '}
                  active
                </span>
                <span className="w-px h-3 bg-light-border dark:border-dark-border" />
                <span className="text-light-subtle dark:text-dark-subtle">
                  <span className="font-bold text-amber-600 dark:text-amber-400">{low}</span>{' '}
                  low
                </span>
                <span className="w-px h-3 bg-light-border dark:border-dark-border" />
                <span className="text-light-subtle dark:text-dark-subtle">
                  <span className="font-bold text-red-600 dark:text-red-400">{out}</span>{' '}
                  out
                </span>
                <span className="ml-auto">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-1.5 text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text"
                >
                  <RefreshCw
                    size={13}
                    className={refreshing ? 'animate-spin' : ''}
                  />
                  Refresh
                </button>
              </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area - Split Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Pane - Product Grid */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="px-4 pt-4">
        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 size={40} className="animate-spin text-primary dark:text-golden" />
          </div>
        )}

        {/* Empty State */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Package size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-lg text-light-subtle dark:text-dark-subtle">
              {search ? 'No products found' : 'No products added yet'}
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle opacity-70">
              {search
                ? 'Try adjusting your search'
                : 'Create your first product to get started'}
            </p>
            {!search && (
              <button
                onClick={() => navigate(`/business/${businessId}/products/new`)}
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-white font-bold bg-primary hover:bg-primary/90 transition-colors"
              >
                <Plus size={18} />
                Add First Product
              </button>
            )}
          </div>
        )}

        {/* Products Grid/List */}
        {!loading && filtered.length > 0 && (
          <>
            {/* 1-col list view */}
            {cols === 1 && (
              <div className="space-y-3">
                {filtered.map((item) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    cols={1}
                    isSelectMode={isSelectMode}
                    isSelected={selectedProductIds.includes(item.id)}
                    onToggleSelect={() => {
                      if (selectedProductIds.includes(item.id)) {
                        setSelectedProductIds(selectedProductIds.filter(id => id !== item.id));
                      } else {
                        setSelectedProductIds([...selectedProductIds, item.id]);
                      }
                    }}
                    onPress={() => {
                      if (isPosLocked) {
                        addToCart(item);
                      } else {
                        navigate(`/business/${businessId}/products/${item.id}`);
                      }
                    }}
                    onCart={() => addToCart(item)}
                  />
                ))}
              </div>
            )}

            {/* Multi-column grid */}
            {cols > 1 && (
              <div className="flex flex-wrap gap-3">
                {filtered.map((item) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    cols={cols}
                    isSelectMode={isSelectMode}
                    isSelected={selectedProductIds.includes(item.id)}
                    onToggleSelect={() => {
                      if (selectedProductIds.includes(item.id)) {
                        setSelectedProductIds(selectedProductIds.filter(id => id !== item.id));
                      } else {
                        setSelectedProductIds([...selectedProductIds, item.id]);
                      }
                    }}
                    onPress={() => {
                      if (isPosLocked) {
                        addToCart(item);
                      } else {
                        navigate(`/business/${businessId}/products/${item.id}`);
                      }
                    }}
                    onCart={() => addToCart(item)}
                  />
                ))}
              </div>
            )}
          </>
        )}
          </div>
        </div>

        {/* Right Pane - POS Sidebar with VS Code-style resize handle */}
        <div
          className="hidden lg:flex flex-shrink-0 relative"
          style={{ width: sidebarCollapsed ? 64 : sidebarWidth }}
        >
          {/* Drag handle */}
          {!sidebarCollapsed && (
            <div
              onMouseDown={handleDragStart}
              className="absolute left-0 top-0 bottom-0 w-1 z-20 cursor-col-resize group hover:bg-primary/40 dark:hover:bg-golden/40 transition-colors"
              title="Drag to resize"
            >
              {/* VS Code-style resize indicator */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full bg-light-border dark:bg-dark-border group-hover:bg-primary dark:group-hover:bg-golden transition-colors" />
            </div>
          )}
          <div className="flex-1 border-l border-light-border dark:border-dark-border shadow-lg overflow-hidden">
            <POSCartSidebar
              businessId={businessId!}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { ProductList };
export default ProductList;
